
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, MessageSquare, CheckCircle, AlertCircle, X } from "lucide-react";
import { format } from "date-fns";

export default function ManageTickets() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [message, setMessage] = useState(null);
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const isAdmin = user?.role === 'admin';

  const { data: tickets = [], isLoading } = useQuery({
    queryKey: ['supportTickets'],
    queryFn: () => base44.entities.SupportTicket.list('-created_date'),
  });

  const updateTicketMutation = useMutation({
    mutationFn: async ({ ticketId, data }) => {
      return base44.entities.SupportTicket.update(ticketId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['supportTickets']);
      setMessage({ type: 'success', text: 'Ticket updated successfully!' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err) => {
      setMessage({ type: 'error', text: `Failed to update ticket: ${err.message}` });
      setTimeout(() => setMessage(null), 5000);
    }
  });

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.submitter_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || ticket.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const handleStatusChange = (ticketId, newStatus) => {
    const updateData = { status: newStatus };
    if (newStatus === 'resolved' || newStatus === 'closed') {
      updateData.resolved_date = new Date().toISOString();
    }
    updateTicketMutation.mutate({ ticketId, data: updateData });
  };

  const handlePriorityChange = (ticketId, newPriority) => {
    updateTicketMutation.mutate({ ticketId, data: { priority: newPriority } });
  };

  const handleSaveNotes = () => {
    if (!selectedTicket) return;
    updateTicketMutation.mutate({ 
      ticketId: selectedTicket.id, 
      data: { admin_notes: adminNotes } 
    });
    setSelectedTicket(null);
    setAdminNotes("");
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { color: "bg-red-500 text-white", label: "New" },
      in_progress: { color: "bg-blue-500 text-white", label: "In Progress" },
      resolved: { color: "bg-green-500 text-white", label: "Resolved" },
      closed: { color: "bg-slate-500 text-white", label: "Closed" }
    };
    return badges[status] || badges.new;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      low: { color: "bg-slate-100 text-slate-800", label: "Low" },
      medium: { color: "bg-yellow-100 text-yellow-800", label: "Medium" },
      high: { color: "bg-orange-100 text-orange-800", label: "High" },
      urgent: { color: "bg-red-100 text-red-800", label: "Urgent" }
    };
    return badges[priority] || badges.medium;
  };

  const getTypeIcon = (type) => {
    const icons = {
      bug_report: "🐛",
      technical_issue: "⚙️",
      feature_request: "✨",
      other: "📝"
    };
    return icons[type] || "📝";
  };

  const newTicketsCount = tickets.filter(t => t.status === 'new').length;
  const inProgressCount = tickets.filter(t => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length;

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            You do not have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <MessageSquare className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Support Tickets</h1>
            <p className="text-slate-600 mt-1">Manage User Support Requests</p>
          </div>
        </div>

        {message && (
          <Alert className={message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}>
            {message.type === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600" />
            )}
            <AlertDescription className={message.type === 'success' ? 'text-green-900' : 'text-red-900'}>
              {message.text}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-500 to-red-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">New Tickets</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{newTicketsCount}</div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">In Progress</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{inProgressCount}</div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Resolved</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-4xl font-bold">{resolvedCount}</div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Submitted By</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        Loading tickets...
                      </TableCell>
                    </TableRow>
                  ) : filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                        No tickets found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((ticket) => {
                      const statusBadge = getStatusBadge(ticket.status);
                      const priorityBadge = getPriorityBadge(ticket.priority);
                      
                      return (
                        <TableRow key={ticket.id} className="hover:bg-slate-50">
                          <TableCell>
                            <span className="text-2xl">{getTypeIcon(ticket.ticket_type)}</span>
                          </TableCell>
                          <TableCell className="font-medium max-w-xs truncate">
                            {ticket.title}
                          </TableCell>
                          <TableCell>{ticket.submitter_name}</TableCell>
                          <TableCell>
                            <Badge className="bg-slate-100 text-slate-800">
                              {ticket.submitter_role}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {format(new Date(ticket.created_date), 'MMM d, yyyy')}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={ticket.status}
                              onValueChange={(value) => handleStatusChange(ticket.id, value)}
                            >
                              <SelectTrigger className={`w-36 ${statusBadge.color}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="new">New</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="resolved">Resolved</SelectItem>
                                <SelectItem value="closed">Closed</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={ticket.priority}
                              onValueChange={(value) => handlePriorityChange(ticket.id, value)}
                            >
                              <SelectTrigger className={`w-32 ${priorityBadge.color}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">Low</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="urgent">Urgent</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedTicket(ticket);
                                setAdminNotes(ticket.admin_notes || "");
                              }}
                            >
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {selectedTicket && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
            <Card className="w-full max-w-3xl my-8">
              <CardHeader className="border-b">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-2xl">{getTypeIcon(selectedTicket.ticket_type)}</span>
                    Ticket Details
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedTicket(null)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Title</h3>
                  <p className="text-slate-700">{selectedTicket.title}</p>
                </div>

                <div>
                  <h3 className="font-semibold text-slate-900 mb-1">Description</h3>
                  <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                </div>

                {selectedTicket.screenshot_url && (
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">Screenshot</h3>
                    <img 
                      src={selectedTicket.screenshot_url} 
                      alt="Ticket screenshot" 
                      className="max-w-full rounded border border-slate-200"
                    />
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Submitted By</h3>
                    <p className="text-slate-700">{selectedTicket.submitter_name}</p>
                    <p className="text-sm text-slate-500">{selectedTicket.submitter_email}</p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">Submitted On</h3>
                    <p className="text-slate-700">
                      {format(new Date(selectedTicket.created_date), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <h3 className="font-semibold text-slate-900 mb-2">Admin Notes</h3>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this ticket..."
                    className="h-32"
                  />
                </div>

                <div className="flex gap-3 justify-end pt-4 border-t">
                  <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                    Close
                  </Button>
                  <Button 
                    onClick={handleSaveNotes}
                    className="bg-blue-600 hover:bg-blue-700"
                    disabled={updateTicketMutation.isPending}
                  >
                    Save Notes
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
